import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useApp } from "@/context/AppContext";

const NAV = [
  { to: "/app", key: "nav_home", icon: "fa-house" },
  { to: "/app/cinevision", key: "sec_cinevision_t", icon: "fa-clapperboard" },
  { to: "/app/analysis", key: "sec_analysis_t", icon: "fa-brain" },
  { to: "/app/camera", key: "nav_camera", icon: "fa-vr-cardboard" },
  { to: "/app/templates", key: "nav_templates", icon: "fa-clapperboard" },
  { to: "/app/filters", key: "nav_filters", icon: "fa-wand-magic-sparkles" },
  { to: "/app/training", key: "nav_training", icon: "fa-dumbbell" },
  { to: "/app/settings", key: "settings", icon: "fa-gear" },
];

export default function PresentationShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { togglePresentation } = useApp();
  const rtl = lang === "ar";
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("pm-active");
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      document.documentElement.classList.remove("pm-active");
      document.removeEventListener("fullscreenchange", onFs);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const toggleFullscreen = () => {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen && document.exitFullscreen();
      } else {
        const el = document.documentElement;
        (el.requestFullscreen || el.webkitRequestFullscreen || (() => {})).call(el);
      }
    } catch (e) {}
  };

  const exit = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    togglePresentation();
  };

  return (
    <div className="pm-root" data-testid="presentation-mode" dir={rtl ? "rtl" : "ltr"}>
      {/* شريط علوي أفقي أنيق: الهوية + التنقل + التحكم */}
      <header className="pm-topbar">
        <div className="pm-brand">
          <div className="pm-logo">
            <span className="pm-logo-badge">CINEARt AI</span>
            <span className="pm-subtitle">{t("appSubtitle")}</span>
          </div>
          <div className="pm-researcher" data-testid="pm-researcher">
            <span className="pm-r-name">Mohanad Khaleel Ibrahim</span>
            <span className="pm-r-ar">{t("researcherLine")}</span>
          </div>
        </div>

        <nav className="pm-nav" data-testid="pm-sidebar">
          {NAV.map((item) => {
            const on = item.to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(item.to);
            return (
              <button
                key={item.to}
                data-testid={`pm-nav-${item.key}`}
                onClick={() => navigate(item.to)}
                title={t(item.key)}
                className={`pm-nav-btn ${on ? "pm-nav-on" : ""}`}
              >
                <i className={`fa-solid ${item.icon}`} />
                <span>{t(item.key)}</span>
              </button>
            );
          })}
        </nav>

        <div className="pm-actions">
          <button onClick={toggleFullscreen} data-testid="toggle-fullscreen" title={t("fullscreen")} className="pm-btn pm-fullscreen">
            <i className={`fa-solid ${isFs ? "fa-compress" : "fa-expand"}`} />
            <span>{isFs ? t("exitFullscreen") : t("fullscreen")}</span>
          </button>
          <button onClick={exit} data-testid="exit-presentation" title={t("exitPresentation")} className="pm-btn pm-exit">
            <i className="fa-solid fa-xmark" />
            <span>{t("exitPresentation")}</span>
          </button>
        </div>
      </header>

      {/* المسرح الرئيسي: يأخذ باقي الشاشة */}
      <main className="pm-main" data-testid="pm-main">
        <div className="pm-content">{children}</div>
      </main>
    </div>
  );
}
